<script setup lang="ts">
import { useAuth } from "~/composible/useAuth";
import useOneLink from "~/composible/useOneLink";
import { useApiFetch } from "~/composible/useApiFetch";

const { $toast } = useNuxtApp();
const { user, totalBalance, getOTT, openAuthModal } = useAuth();
const { getAuthBookLink } = useOneLink();

const emit = defineEmits(['chapterUnlocked']);

interface IProps {
  bookId: number,
  chapterId: number,
  coins: number,
}

const props = defineProps<IProps>();

const ott = ref<string|null>(null);
const uuid = ref<string|null>(user.value?.uuid || null)

const loading = ref(false);

const goApp = async () => {
  loading.value = true;

  try {
    if (user.value) {
      const response = await getOTT();
      ott.value = response.data.token;
    }

    window.location.href = getAuthBookLink(props.bookId, ott.value, uuid.value);
  } catch(error) {
    $toast.warning('Something went wrong.')
  } finally {
    loading.value = false;
  }
}

const unlock = async () => {
  loading.value = true;

  const {data, error} = await useApiFetch(`chapters/${props.chapterId}/unlock`, {
    method: 'POST'
  });

  if (!error.value) {
    user.value.balance = data.value.data.balance;

    emit('chapterUnlocked');
  } else {
    const errorMessage = error.value.data?.message || 'Something went wrong.';
    $toast.warning(errorMessage)
  }

  loading.value = false;
}

</script>

<template>
  <div class="relative mb-8">
    <div class="absolute top-[-100px] w-full h-[100px] z-0 bg-chapter-crop dark:bg-chapter-crop-dark"></div>
    <div v-loading="loading" class="flex flex-col justify-center items-center gap-6 max-md:gap-4 m-auto max-w-[500px] px-8 py-6 bg-white dark:bg-zinc-800 border border-gray-200 dark:border-zinc-600 rounded-xl text-center drop-shadow-xl">
      <template v-if="user && totalBalance >= coins">
        <nuxt-img
            alt="Padlock with kaey"
            src="/img/padlock_with_key.svg"
            width="80"
            height="80"
        />

        <div class="text-3xl max-md:text-2xl font-bold space-y-2">
          <p class="text-primary">{{ coins }} coins or bonuses</p>
          <p>to read this chapter</p>
        </div>

        <div class="flex flex-col gap-4 max-md:gap-2 w-full">
          <button
              @click.prevent="unlock"
              class="flex w-full items-center py-3 px-8 max-md:px-4 bg-fm-gradient hover:bg-fm-gradient-active text-center justify-center rounded-xl !text-white text-xl max-md:text-xl"
          >
            Unlock now
          </button>

          <div class="text-xl text-gray-400">OR</div>

          <button
              @click.prevent="goApp"
              class="flex w-full items-center py-3 px-8 max-md:px-4 justify-center text-xl max-md:text-xl border-2 border-primary text-center rounded-xl text-violet-800 dark:text-violet-500 dark:hover:text-violet-600"
          >
            Continue Reading in the 📱 App
          </button>
        </div>

        <div class="text-xl">Balance: <span class="font-semibold">{{ user.balance.coins }} Coins | {{ user.balance.bonus }} Bonuses</span></div>
      </template>

      <template v-else-if="user && totalBalance < coins">
        <nuxt-img
            alt="Coin"
            src="/img/coin.svg"
            width="80"
            height="80"
        />

        <div class="space-y-3">
          <p class="text-3xl max-md:text-2xl font-bold">You have not enough coins</p>
          <p class="text-lg text-gray-500">Top Up your balance to unlock chapters</p>
        </div>

        <router-link
            to="/profile/balance"
            class="flex w-full items-center py-3 px-8 max-md:px-4 bg-fm-gradient hover:bg-fm-gradient-active text-center justify-center rounded-xl !text-white text-xl max-md:text-xl"
        >
          Top Up
        </router-link>

        <div class="text-xl">Balance: <span class="font-semibold">{{ user.balance.coins }} Coins | {{ user.balance.bonus }} Bonuses</span></div>
      </template>

      <template v-else>
        <nuxt-img
            alt="Padlock with kaey"
            src="/img/padlock_with_key.svg"
            width="80"
            height="80"
        />

        <div class="space-y-4">
          <p class="text-3xl max-md:text-2xl font-bold leading-10">Did you enjoy reading <br>this book?</p>
          <p class="text-lg text-gray-500">Create an account to unlock this chapter</p>
        </div>

        <div class="flex flex-col gap-4 max-md:gap-2 w-full">
          <button
              @click.prevent="openAuthModal"
              class="flex flex-col w-full items-center py-1 px-8 max-md:px-4 bg-fm-gradient hover:bg-fm-gradient-active text-center justify-center rounded-xl !text-white text-xl max-md:text-xl"
          >
            <span>Unlock this chapter</span>
            <span class="text-lg font-light">for {{ coins }} Coins</span>
          </button>

          <div class="text-xl text-gray-400">OR</div>

          <button
              @click.prevent="goApp"
              class="flex w-full items-center py-3 px-8 max-md:px-4 justify-center text-xl max-md:text-xl border-2 border-primary text-center rounded-xl text-violet-800 dark:text-violet-500 dark:hover:text-violet-600"
          >
            Continue Reading in the 📱 App
          </button>
        </div>
      </template>
    </div>
  </div>
</template>

<style scoped>

</style>